import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Eye, DollarSign, Users, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { formatCents } from '../../utils/credits';
import { apiEndpoints } from '../../utils/api';

interface SubscriptionTier {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  price: number;
  included_credits: number;
  features: string[];
  is_active: boolean;
  is_default: boolean;
  sort_order: number;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface SubscriptionTierUsageStats {
  tier_id: string;
  tier_name: string;
  total_subscriptions: number;
  active_subscriptions: number;
  status_breakdown: Record<string, number>;
  can_delete: boolean;
}

interface SubscriptionTierCreateRequest {
  name: string;
  display_name: string;
  description?: string;
  price: number;
  included_credits: number;
  features: string[];
  is_active: boolean;
  sort_order: number;
  metadata?: Record<string, any>;
}

const SubscriptionTierSection: React.FC = () => {
  const authenticatedApi = useAuthenticatedApi();
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [usageStats, setUsageStats] = useState<SubscriptionTierUsageStats | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [pendingDefaultTier, setPendingDefaultTier] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<SubscriptionTierCreateRequest>({
    name: '',
    display_name: '',
    description: '',
    price: 0,
    included_credits: 0,
    features: [],
    is_active: false,
    sort_order: 0,
    metadata: {}
  });

  // Auto-dismiss error messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchTiers = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }
    
    try {
      const data = await authenticatedApi.get<{tiers: SubscriptionTier[]}>(
        `${apiEndpoints.admin.subscriptionTiers}?include_inactive=${includeInactive}`
      );
      setTiers(data.tiers);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch tiers');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      }
    }
  }, [includeInactive, authenticatedApi]);

  const fetchUsageStats = async (tierId: string) => {
    try {
      const stats = await authenticatedApi.get<SubscriptionTierUsageStats>(
        apiEndpoints.admin.subscriptionTierUsage(tierId)
      );
      setUsageStats(stats);
    } catch (err) {
      console.error('Error fetching usage stats:', err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null); // Clear any previous modal errors
    try {
      const dataToSend = {
        ...formData,
        features: getFilteredFeatures(formData.features)
      };
      
      await authenticatedApi.post<SubscriptionTier>(
        apiEndpoints.admin.subscriptionTiers,
        dataToSend
      );

      setShowCreateModal(false);
      resetForm();
      fetchTiers();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTier) return;
    setModalError(null); // Clear any previous modal errors

    try {
      // Exclude name field from updates (immutable) and filter features
      const { name, ...updateData } = formData;
      const dataToSend = {
        ...updateData,
        features: getFilteredFeatures(formData.features)
      };
      
      await authenticatedApi.put<SubscriptionTier>(
        apiEndpoints.admin.subscriptionTier(selectedTier.id),
        dataToSend
      );

      setShowEditModal(false);
      setSelectedTier(null);
      resetForm();
      fetchTiers();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDeleteClick = (tier: SubscriptionTier) => {
    setSelectedTier(tier);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTier) return;

    try {
      const result = await authenticatedApi.delete<{success: boolean; message?: string}>(
        apiEndpoints.admin.subscriptionTier(selectedTier.id)
      );
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to delete tier');
      }

      setShowDeleteModal(false);
      setSelectedTier(null);
      fetchTiers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setShowDeleteModal(false);
      setSelectedTier(null);
    }
  };

  const handleSetDefaultClick = (tier: SubscriptionTier) => {
    if (tier.is_default) {
      return; // Already default, do nothing
    }
    
    if (!tier.is_active) {
      setError('Cannot set an inactive tier as default');
      return;
    }
    
    if (pendingDefaultTier === tier.id) {
      // Second click - confirm the action
      handleSetDefaultConfirm(tier);
    } else {
      // First click - set as pending
      setPendingDefaultTier(tier.id);
      // Clear pending state after 2 seconds
      setTimeout(() => setPendingDefaultTier(null), 2000);
    }
  };

  const handleSetDefaultConfirm = async (tier: SubscriptionTier) => {
    try {
      await authenticatedApi.post(
        apiEndpoints.admin.subscriptionTierSetDefault(tier.id)
      );
      
      setPendingDefaultTier(null);
      fetchTiers(true); // Refresh to show updated default status
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set tier as default');
      setPendingDefaultTier(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      price: 0,
      included_credits: 0,
      features: [''], // Start with one empty feature field
      is_active: true,
      sort_order: 0,
      metadata: {}
    });
  };

  const openCreateModal = () => {
    setModalError(null); // Clear any previous modal errors
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (tier: SubscriptionTier) => {
    setModalError(null); // Clear any previous modal errors
    setSelectedTier(tier);
    
    // Ensure there's always an empty field at the end for new features
    const featuresWithEmpty = [...tier.features];
    if (featuresWithEmpty.length === 0 || featuresWithEmpty[featuresWithEmpty.length - 1].trim() !== '') {
      featuresWithEmpty.push('');
    }
    
    setFormData({
      name: tier.name,
      display_name: tier.display_name,
      description: tier.description || '',
      price: tier.price,
      included_credits: tier.included_credits,
      features: featuresWithEmpty,
      is_active: tier.is_active,
      sort_order: tier.sort_order,
      metadata: tier.metadata
    });
    setShowEditModal(true);
  };

  const openDetailsModal = (tier: SubscriptionTier) => {
    setSelectedTier(tier);
    fetchUsageStats(tier.id);
    setShowDetailsModal(true);
  };

  const updateFeature = (index: number, value: string) => {
    setFormData(prev => {
      const newFeatures = prev.features.map((feature, i) => i === index ? value : feature);
      
      // If this is the last field and it's not empty, add a new empty field
      if (index === newFeatures.length - 1 && value.trim() !== '') {
        newFeatures.push('');
      }
      
      return {
        ...prev,
        features: newFeatures
      };
    });
  };

  const removeFeature = (index: number) => {
    setFormData(prev => {
      const newFeatures = prev.features.filter((_, i) => i !== index);
      
      // Ensure there's always at least one empty field
      if (newFeatures.length === 0 || newFeatures.every(f => f.trim() !== '')) {
        newFeatures.push('');
      }
      
      return {
        ...prev,
        features: newFeatures
      };
    });
  };

  // Helper function to filter out empty features before sending to backend
  const getFilteredFeatures = (features: string[]) => {
    return features.filter(feature => feature.trim() !== '');
  };

  // Handle keyboard navigation in feature fields
  const handleFeatureKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      
      // If this is the last field and it's not empty, a new field will be created by updateFeature
      // Focus on the next field after state update
      setTimeout(() => {
        const nextIndex = index + 1;
        const nextInput = document.querySelector(`input[data-feature-index="${nextIndex}"]`) as HTMLInputElement;
        if (nextInput) {
          nextInput.focus();
        }
      }, 0);
    }
  };

  // Initial load
  useEffect(() => {
    fetchTiers(false);
  }, [fetchTiers]);

  // Refresh when includeInactive changes (but not on initial load)
  useEffect(() => {
    if (tiers.length > 0) { // Only refresh if we already have data
      fetchTiers(true);
    }
  }, [includeInactive, fetchTiers, tiers.length]);



  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white">
          Subscription Tiers
        </h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <button
            onClick={() => fetchTiers(true)}
            disabled={refreshing}
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh tiers"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center justify-center gap-2 bg-brand-primary text-black px-4 py-2 rounded-lg hover:bg-brand-primary/90 transition-colors"
          >
            <Plus size={18} />
            Create Tier
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle size={20} className="text-red-500 mr-2" />
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="mb-6 flex items-center gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded border-neutral-300 dark:border-neutral-600"
          />
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            Include inactive tiers
          </span>
        </label>
        {refreshing && (
          <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary"></div>
            <span>Refreshing...</span>
          </div>
        )}
      </div>

      {/* Tiers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className={`bg-white dark:bg-neutral-800 rounded-xl border ${
              tier.is_active 
                ? 'border-slate-300 dark:border-neutral-700' 
                : 'border-red-200 dark:border-red-800 opacity-75'
            } shadow-sm p-6 flex flex-col h-full`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                  {tier.display_name}
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {tier.name}
                </p>

              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openDetailsModal(tier)}
                  className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                  title="View tier details"
                >
                  <Eye size={16} />
                </button>
                <button
                  onClick={() => openEditModal(tier)}
                  className="p-2 text-neutral-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  title="Edit tier"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDeleteClick(tier)}
                  className="p-2 text-neutral-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Delete tier"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="space-y-3 flex-grow flex flex-col">
              <div className="flex-grow space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Price</span>
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    ${tier.price}/month
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Credits</span>
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    ${tier.included_credits}
                  </span>
                </div>

                {tier.description && (
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
                    {tier.description}
                  </p>
                )}

                {tier.features.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">Features:</p>
                    <ul className="text-xs text-neutral-600 dark:text-neutral-400 space-y-1">
                      {tier.features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <CheckCircle size={12} className="text-green-500 mr-1 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                      {tier.features.length > 3 && (
                        <li className="text-neutral-400">
                          +{tier.features.length - 3} more features
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700 mt-auto space-y-2">
                {/* Active/Inactive Status */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">Status</span>
                  <span className={`text-xs font-medium ${
                    tier.is_active 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {tier.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                {/* Default Tier Status */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">Default</span>
                  {tier.is_default ? (
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                      Yes
                    </span>
                  ) : (
                    <button
                      onClick={() => handleSetDefaultClick(tier)}
                      disabled={!tier.is_active}
                      className={`text-xs font-medium transition-colors ${
                        !tier.is_active
                          ? 'text-neutral-400 dark:text-neutral-600 cursor-not-allowed'
                          : pendingDefaultTier === tier.id
                          ? 'text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300'
                          : 'text-neutral-600 dark:text-neutral-400 hover:text-blue-600 dark:hover:text-blue-400'
                      }`}
                      title={!tier.is_active ? 'Cannot set inactive tier as default' : pendingDefaultTier === tier.id ? 'Click again to confirm' : 'Click to set as default'}
                    >
                      {pendingDefaultTier === tier.id ? 'Confirm?' : 'No'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {tiers.length === 0 && (
        <div className="text-center py-12">
          <DollarSign size={48} className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
            No subscription tiers found
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            Create your first subscription tier to get started.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-brand-primary text-black px-4 py-2 rounded-lg hover:bg-brand-primary/90 transition-colors"
          >
            Create Tier
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                {showCreateModal ? 'Create Subscription Tier' : 'Edit Subscription Tier'}
              </h3>
            </div>
            
            <form onSubmit={showCreateModal ? handleCreate : handleEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    disabled={showEditModal}
                    className={`w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white ${
                      showEditModal 
                        ? 'bg-neutral-100 dark:bg-neutral-600 cursor-not-allowed' 
                        : 'bg-white dark:bg-neutral-700'
                    }`}
                    placeholder="e.g., pro, enterprise"
                  />
                  {showCreateModal && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 leading-relaxed">
                      ⚠️ Name cannot be changed once created (maintains referential integrity)
                    </p>
                  )}
                  {showEditModal && (
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                      Name cannot be modified to maintain referential integrity with subscriptions
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.display_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                    placeholder="e.g., Pro Plan"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                  rows={3}
                  placeholder="Describe this subscription tier..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Price (USD) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Credits *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.included_credits}
                    onChange={(e) => setFormData(prev => ({ ...prev, included_credits: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Features
                </label>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        onKeyDown={(e) => handleFeatureKeyDown(e, index)}
                        data-feature-index={index}
                        className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                        placeholder={index === formData.features.length - 1 && feature === '' ? "Enter new feature..." : "Enter feature..."}
                      />
                      {/* Only show remove button for non-empty fields or if there are multiple fields */}
                      {(feature.trim() !== '' || formData.features.length > 1) && (
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="p-2 text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                      {/* Show placeholder for remove button to maintain consistent spacing */}
                      {feature.trim() === '' && formData.features.length === 1 && (
                        <div className="w-10 h-10"></div>
                      )}
                    </div>
                  ))}
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    💡 A new feature field will automatically appear when you fill in the current one
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-neutral-300 dark:border-neutral-600"
                  />
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">Active</span>
                </label>
              </div>

              {/* Modal Error Display */}
              {modalError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{modalError}</p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedTier(null);
                    setModalError(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-brand-primary text-black px-4 py-2 rounded-lg hover:bg-brand-primary/90 transition-colors"
                >
                  {showCreateModal ? 'Create Tier' : 'Update Tier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tier Details Modal */}
      {showDetailsModal && selectedTier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">
                {selectedTier.display_name}
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                Tier Details & Usage Statistics
              </p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Internal Name</p>
                    <p className="text-base font-medium text-neutral-900 dark:text-white">{selectedTier.name}</p>
                  </div>
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Display Name</p>
                    <p className="text-base font-medium text-neutral-900 dark:text-white">{selectedTier.display_name}</p>
                  </div>
                  {selectedTier.description && (
                    <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg md:col-span-2">
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">Description</p>
                      <p className="text-base text-neutral-900 dark:text-white">{selectedTier.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing & Credits */}
              <div>
                <h4 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Pricing & Credits</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Monthly Price</p>
                    <p className="text-2xl font-semibold text-neutral-900 dark:text-white">${selectedTier.price}</p>
                  </div>
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Included Credits</p>
                    <p className="text-2xl font-semibold text-neutral-900 dark:text-white">${selectedTier.included_credits}</p>
                  </div>
                </div>
              </div>

              {/* Features */}
              {selectedTier.features.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Features</h4>
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <ul className="space-y-2">
                      {selectedTier.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-neutral-700 dark:text-neutral-300">
                          <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Status & Settings */}
              <div>
                <h4 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Status & Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Status</p>
                    <div className="flex items-center mt-1">
                      <div className={`w-2 h-2 rounded-full mr-2 ${
                        selectedTier.is_active ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <p className="text-base font-medium text-neutral-900 dark:text-white">
                        {selectedTier.is_active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Default Tier</p>
                    <div className="flex items-center mt-1">
                      <div className={`w-2 h-2 rounded-full mr-2 ${
                        selectedTier.is_default ? 'bg-blue-500' : 'bg-neutral-400'
                      }`}></div>
                      <p className="text-base font-medium text-neutral-900 dark:text-white">
                        {selectedTier.is_default ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Sort Order</p>
                    <p className="text-base font-medium text-neutral-900 dark:text-white">{selectedTier.sort_order}</p>
                  </div>
                </div>
              </div>

              {/* Usage Statistics */}
              <div>
                <h4 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Usage Statistics</h4>
                {usageStats ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                        <div className="flex items-center">
                          <Users size={20} className="text-blue-500 mr-2" />
                          <div>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Subscriptions</p>
                            <p className="text-xl font-semibold text-neutral-900 dark:text-white">
                              {usageStats.total_subscriptions}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                        <div className="flex items-center">
                          <CheckCircle size={20} className="text-green-500 mr-2" />
                          <div>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">Active Subscriptions</p>
                            <p className="text-xl font-semibold text-neutral-900 dark:text-white">
                              {usageStats.active_subscriptions}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {Object.keys(usageStats.status_breakdown).length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                          Status Breakdown
                        </h5>
                        <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                          <div className="space-y-2">
                            {Object.entries(usageStats.status_breakdown).map(([status, count]) => (
                              <div key={status} className="flex items-center justify-between">
                                <span className="text-sm text-neutral-600 dark:text-neutral-400 capitalize">
                                  {status}
                                </span>
                                <span className="text-sm font-medium text-neutral-900 dark:text-white">
                                  {count}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className={`p-3 rounded-lg ${
                      usageStats.can_delete 
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    }`}>
                      <div className="flex items-center">
                        {usageStats.can_delete ? (
                          <CheckCircle size={16} className="text-green-500 mr-2" />
                        ) : (
                          <AlertTriangle size={16} className="text-red-500 mr-2" />
                        )}
                        <p className={`text-sm ${
                          usageStats.can_delete 
                            ? 'text-green-700 dark:text-green-300'
                            : 'text-red-700 dark:text-red-300'
                        }`}>
                          {usageStats.can_delete 
                            ? 'This tier can be safely deleted'
                            : 'This tier cannot be deleted due to active subscriptions'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                  </div>
                )}
              </div>

              {/* Metadata */}
              {Object.keys(selectedTier.metadata).length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Metadata</h4>
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <pre className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">
                      {JSON.stringify(selectedTier.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div>
                <h4 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Timestamps</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Created At</p>
                    <p className="text-base text-neutral-900 dark:text-white">
                      {new Date(selectedTier.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Last Updated</p>
                    <p className="text-base text-neutral-900 dark:text-white">
                      {new Date(selectedTier.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-neutral-200 dark:border-neutral-700">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedTier(null);
                  setUsageStats(null);
                }}
                className="w-full bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-white px-4 py-2 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle size={24} className="text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Delete Subscription Tier
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-neutral-600 dark:text-neutral-400 mb-2">
                Are you sure you want to delete the <strong>"{selectedTier.display_name}"</strong> tier?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">
                This action cannot be undone. If there are active subscriptions using this tier, the deletion will be prevented.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedTier(null);
                }}
                className="px-4 py-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Tier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionTierSection;
